import React, { useContext, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
    addQuestionToSet, duplicateCompanyQuestion,
    removeQuestionFromSet, addSkillToCompanyQuestion
} from 'requests/CMSRequests';

import { appCtx } from 'components/appStore';
import ItemActions from './ItemActions';
import RemoveQuestionDialog from './RemoveQuestionDialog';
import AddSkillDialog from './AddSkillDialog';

const QuestionActions = observer(({
    loadQuestions, question, hideMenu, currentPage,
    setSelectedQuestionId, selectedSet,
    setAddToSetQuestionUuid, setRemoveFromSetQuestionUuid, setCurrentPage,
    assessmentPage, hideEdit, hideReorder, hideAdd, hideDuplicate,
    showDone, setQuestionState, deleteCallback
}) => {
    const [openRemoveDialog, setOpenRemoveDialog] = useState(false);
    const [openSkillDialog, setOpenSkillDialog] = useState(false);
    const [loadingDelete, setLoadingDelete] = useState(false);
    const [loadingAdd, setLoadingAdd] = useState(false);
    const [loadingAddSkill, setLoadingAddSkill] = useState(false);
    const { flashMessage } = useContext(appCtx);

    const { id, uuid, setsUsing, companySkills } = question;

    const onEdit = () => {
        setSelectedQuestionId(id);
    };

    const handleRemoveDialog = () => {
        setOpenRemoveDialog(!openRemoveDialog);
    };

    const handleSkillDialog = () => {
        setOpenSkillDialog(!openSkillDialog);
    };

    const onDuplicate = () => {
        flashMessage('Duplicating question', 'pending', 100000);
        duplicateCompanyQuestion(id)
            .then(() => {
                if (currentPage === 1 && loadQuestions) loadQuestions();
                else if (setCurrentPage) setCurrentPage(1);
                flashMessage('Question duplicated', 'done');
            })
            .catch(() => {
                flashMessage('Something went wrong', 'error');
            });
    };

    const onAdd = () => {
        if (loadingAdd) return;
        setLoadingAdd(true);
        addQuestionToSet(selectedSet.uuid, uuid)
            .then(({ data, success }) => {
                if (success && data) {
                    if (setQuestionState) setQuestionState(data);
                    if (setAddToSetQuestionUuid) setAddToSetQuestionUuid(uuid);
                    handleUnmatchedSkill();
                    flashMessage('Question added to set', 'done');
                }
            })
            .finally(() => {
                setLoadingAdd(false);
            });
    };

    const onDelete = () => {
        setLoadingDelete(true);
        removeQuestionFromSet(selectedSet.uuid, uuid)
            .then(({ success, data }) => {
                if (success && data) {
                    if (setQuestionState) setQuestionState(data);
                    if (deleteCallback) deleteCallback();
                }
                if (setRemoveFromSetQuestionUuid) setRemoveFromSetQuestionUuid(uuid);
                setOpenRemoveDialog(false);
                flashMessage(`Question removed ${assessmentPage ? '' : 'from set'}`, 'done');
            })
            .finally(() => {
                setLoadingDelete(false);
            });
    };

    const addSkillToQuestion = () => {
        setLoadingAddSkill(true);
        addSkillToCompanyQuestion(id, selectedSet.companySkill.id).then(({ success, data }) => {
            if (success && data) {
                if (setQuestionState) setQuestionState(data);
                setOpenSkillDialog(false);
                flashMessage('Skill added to question', 'done');
            }
        }).catch(() => {
            flashMessage('Something went wrong', 'error');
        }).finally(() => {
            setLoadingAddSkill(false);
        });
    };

    const handleUnmatchedSkill = () => {
        const setSkill = selectedSet.companySkill;
        if (!setSkill || !companySkills) return;
        const isMissedSkill = companySkills.every(({ id: skillId }) => skillId !== setSkill.id);
        if (isMissedSkill) {
            setOpenSkillDialog(true);
        }
    };

    const isInSelectedSet = selectedSet && setsUsing.some(({ uuid: setUuid }) => setUuid === selectedSet.uuid);

    return (
        <>
            {
                !hideMenu && (
                    <ItemActions
                        onEdit={onEdit}
                        onAdd={onAdd}
                        disabledAddToSet={!selectedSet}
                        isInSelectedSet={isInSelectedSet}
                        onDelete={(handleRemoveDialog)}
                        onDuplicate={onDuplicate}
                        hideEdit={hideEdit}
                        hideReorder={hideReorder}
                        hideAdd={hideAdd}
                        hideDuplicate={hideDuplicate}
                        showDone={showDone}
                        loadingAdd={loadingAdd}
                    />
                )
            }
            <RemoveQuestionDialog
                open={openRemoveDialog}
                onRemove={onDelete}
                loading={loadingDelete}
                onClose={handleRemoveDialog}
                assessmentPage={assessmentPage}
            />
            <AddSkillDialog
                open={openSkillDialog}
                onAdd={addSkillToQuestion}
                loading={loadingAddSkill}
                onClose={handleSkillDialog}
                skill={selectedSet?.companySkill.title}
            />
        </>
    );
});

export default QuestionActions;
